import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaPlus,
  FaTrash,
  FaCheck,
  FaExclamationTriangle
} from "react-icons/fa";
import { fetchWithAuth, getApiUrl } from "../../../utils/api";
import {
  HOURS_12,
  MINUTES_60,
  parseTimeTo12Hour,
  format12To24,
  calculateDurationInMinutes
} from "../../../utils/eventTimeUtils";
import "./CreateEventModal.css";

const CreateEventModal = ({
  isOpen,
  onClose,
  onEventCreated,
  onEventUpdated,
  eventToEdit
}) => {
  // Form State
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [shortDescription, setShortDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [category, setCategory] = useState("Web Development");
  const [tagsInput, setTagsInput] = useState("React, JavaScript, Full Stack");

  // Cloudinary Banner
  const [bannerPreview, setBannerPreview] = useState("");
  const [bannerSecureUrl, setBannerSecureUrl] = useState("");
  const [bannerPublicId, setBannerPublicId] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);

  // Date & Time (12-Hour AM/PM Selection)
  const [eventDate, setEventDate] = useState("");
  const [startHour, setStartHour] = useState("10");
  const [startMinute, setStartMinute] = useState("00");
  const [startMeridian, setStartMeridian] = useState("AM");

  const [endHour, setEndHour] = useState("11");
  const [endMinute, setEndMinute] = useState("00");
  const [endMeridian, setEndMeridian] = useState("AM");

  const [durationMinutes, setDurationMinutes] = useState(60);
  const [timezone] = useState("Asia/Kolkata");

  // Format & Meeting Platform
  const [format, setFormat] = useState("Live Online");
  const [meetingPlatform, setMeetingPlatform] = useState("--- Select An Option ---");
  const [privateMeetingUrl, setPrivateMeetingUrl] = useState("");

  // Seats & Language
  const [totalSeats, setTotalSeats] = useState(100);
  const [language, setLanguage] = useState("English");

  // Dynamic Array Fields
  const [learningOutcomes, setLearningOutcomes] = useState([
    "Master advanced state management and architectural patterns",
    "Build full stack production-ready applications with zero latency",
    "Implement real-time features, secure authentication, and cloud deployment"
  ]);
  const [newOutcome, setNewOutcome] = useState("");

  const [targetAudience, setTargetAudience] = useState(
    "Frontend developers, full stack engineers, CS students, and tech enthusiasts looking to master modern web architectures."
  );
  const [prerequisites, setPrerequisites] = useState(
    "Basic knowledge of HTML, CSS, and modern JavaScript (ES6+). No advanced backend experience required."
  );

  const [agenda, setAgenda] = useState([
    { time: "07:00 PM - 07:15 PM", title: "Welcome & Architecture Overview", description: "Introduction to core principles and system setup." },
    { time: "07:15 PM - 08:00 PM", title: "Hands-on Implementation Deep Dive", description: "Live coding and implementing real-world patterns." },
    { time: "08:00 PM - 08:30 PM", title: "Live Q&A & Code Reviews", description: "Interactive participant questions and next steps." }
  ]);
  const [newAgendaTime, setNewAgendaTime] = useState("");
  const [newAgendaTitle, setNewAgendaTitle] = useState("");
  const [newAgendaDesc, setNewAgendaDesc] = useState("");

  // Host Info
  const [hostName, setHostName] = useState("Praveen Kumar B");
  const [hostDesignation, setHostDesignation] = useState("Full Stack Developer & Founder");
  const [hostBio, setHostBio] = useState(
    "Experienced software engineer passionate about building scalable cloud products, SaaS solutions, and mentoring the next generation of engineers."
  );

  // Status & Validation
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Auto-generate slug from title if not manually customized
  useEffect(() => {
    if (!slugEdited && title) {
      const generated = title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
      setSlug(generated);
    }
  }, [title, slugEdited]);

  // Calculate event duration using actual selected start and end times
  useEffect(() => {
    const s24 = format12To24(startHour, startMinute, startMeridian);
    const e24 = format12To24(endHour, endMinute, endMeridian);
    const dur = calculateDurationInMinutes(s24, e24);
    if (dur > 0) {
      setDurationMinutes(dur);
    }
  }, [startHour, startMinute, startMeridian, endHour, endMinute, endMeridian]);

  // Load saved event details when editing or reset for create
  useEffect(() => {
    if (isOpen) {
      if (eventToEdit) {
        setTitle(eventToEdit.title || "");
        setSlug(eventToEdit.slug || "");
        setSlugEdited(true);
        setShortDescription(eventToEdit.shortDescription || "");
        setLongDescription(eventToEdit.longDescription || "");
        setCategory(eventToEdit.category || "Web Development");
        setTagsInput(Array.isArray(eventToEdit.tags) ? eventToEdit.tags.join(", ") : "");
        setBannerPreview(eventToEdit.bannerImage || "");
        setBannerSecureUrl(eventToEdit.bannerImage || "");
        setBannerPublicId(eventToEdit.bannerImagePublicId || "");
        setEventDate(eventToEdit.eventDate || "");

        const parsedStart = parseTimeTo12Hour(eventToEdit.startTime || "10:00 AM");
        setStartHour(parsedStart.hour);
        setStartMinute(parsedStart.minute);
        setStartMeridian(parsedStart.ampm);

        const parsedEnd = parseTimeTo12Hour(eventToEdit.endTime || "11:00 AM");
        setEndHour(parsedEnd.hour);
        setEndMinute(parsedEnd.minute);
        setEndMeridian(parsedEnd.ampm);

        setDurationMinutes(eventToEdit.durationMinutes || 60);
        setFormat(eventToEdit.format || "Live Online");
        setMeetingPlatform(eventToEdit.meetingPlatform || "--- Select An Option ---");
        setPrivateMeetingUrl(eventToEdit.privateMeetingUrl || "");
        setTotalSeats(eventToEdit.totalSeats || 100);
        setLanguage(eventToEdit.language || "English");
        setLearningOutcomes(
          eventToEdit.learningOutcomes?.length
            ? eventToEdit.learningOutcomes
            : [
                "Master advanced state management and architectural patterns",
                "Build full stack production-ready applications with zero latency",
                "Implement real-time features, secure authentication, and cloud deployment"
              ]
        );
        setTargetAudience(eventToEdit.targetAudience || "");
        setPrerequisites(eventToEdit.prerequisites || "");
        setAgenda(eventToEdit.agenda?.length ? eventToEdit.agenda : []);
        setHostName(eventToEdit.hostInformation?.name || "Praveen Kumar B");
        setHostDesignation(eventToEdit.hostInformation?.designation || "Full Stack Developer & Founder");
        setHostBio(eventToEdit.hostInformation?.bio || "");
      } else {
        setTitle("");
        setSlug("");
        setSlugEdited(false);
        setShortDescription("");
        setLongDescription("");
        setCategory("Web Development");
        setTagsInput("React, JavaScript, Full Stack");
        setBannerPreview("");
        setBannerSecureUrl("");
        setBannerPublicId("");
        setEventDate("");
        setStartHour("10");
        setStartMinute("00");
        setStartMeridian("AM");
        setEndHour("11");
        setEndMinute("00");
        setEndMeridian("AM");
        setDurationMinutes(60);
        setFormat("Live Online");
        setMeetingPlatform("--- Select An Option ---");
        setPrivateMeetingUrl("");
        setTotalSeats(100);
        setLanguage("English");
        setLearningOutcomes([
          "Master advanced state management and architectural patterns",
          "Build full stack production-ready applications with zero latency",
          "Implement real-time features, secure authentication, and cloud deployment"
        ]);
        setTargetAudience(
          "Frontend developers, full stack engineers, CS students, and tech enthusiasts looking to master modern web architectures."
        );
        setPrerequisites(
          "Basic knowledge of HTML, CSS, and modern JavaScript (ES6+). No advanced backend experience required."
        );
        setAgenda([
          { time: "07:00 PM - 07:15 PM", title: "Welcome & Architecture Overview", description: "Introduction to core principles and system setup." },
          { time: "07:15 PM - 08:00 PM", title: "Hands-on Implementation Deep Dive", description: "Live coding and implementing real-world patterns." },
          { time: "08:00 PM - 08:30 PM", title: "Live Q&A & Code Reviews", description: "Interactive participant questions and next steps." }
        ]);
        setHostName("Praveen Kumar B");
        setHostDesignation("Full Stack Developer & Founder");
        setHostBio(
          "Experienced software engineer passionate about building scalable cloud products, SaaS solutions, and mentoring the next generation of engineers."
        );
      }
      setErrorMessage("");
    }
  }, [isOpen, eventToEdit]);

  // Cloudinary image upload handler
  const handleBannerSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage("");
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setErrorMessage("Only JPG, PNG, and WebP images are supported.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErrorMessage("Image file size exceeds 8MB limit.");
      return;
    }

    setBannerPreview(URL.createObjectURL(file));

    // Upload directly to backend Cloudinary endpoint
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("banner", file);

      const token = localStorage.getItem("adminToken");
      const res = await fetch(getApiUrl("/admin/events/upload-banner"), {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setBannerSecureUrl(data.secure_url);
        setBannerPublicId(data.public_id || "");
      } else {
        setErrorMessage(data.message || "Failed to upload image to Cloudinary");
      }
    } catch (err) {
      console.error("Banner upload error:", err);
      setErrorMessage("Network error during Cloudinary image upload");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddOutcome = () => {
    if (newOutcome.trim()) {
      setLearningOutcomes([...learningOutcomes, newOutcome.trim()]);
      setNewOutcome("");
    }
  };

  const handleRemoveOutcome = (idx) => {
    setLearningOutcomes(learningOutcomes.filter((_, i) => i !== idx));
  };

  const handleAddAgenda = () => {
    if (newAgendaTitle.trim()) {
      setAgenda([
        ...agenda,
        {
          time: newAgendaTime.trim(),
          title: newAgendaTitle.trim(),
          description: newAgendaDesc.trim()
        }
      ]);
      setNewAgendaTime("");
      setNewAgendaTitle("");
      setNewAgendaDesc("");
    }
  };

  const handleRemoveAgenda = (idx) => {
    setAgenda(agenda.filter((_, i) => i !== idx));
  };

  const validateAllFields = () => {
    if (!title.trim()) return "Event Title is required";
    if (!slug.trim()) return "Event Slug is required";
    if (!shortDescription.trim()) return "Short Description is required";
    if (!longDescription.trim()) return "Long Description is required";
    if (!bannerSecureUrl) return "Event Banner Image is mandatory. Please upload an image.";
    if (!category.trim()) return "Event Category is required";
    if (!eventDate) return "Event Date is required";
    if (!startHour || !startMinute || !startMeridian) return "Start Time is required";
    if (!endHour || !endMinute || !endMeridian) return "End Time is required";

    const s24 = format12To24(startHour, startMinute, startMeridian);
    const e24 = format12To24(endHour, endMinute, endMeridian);
    if (s24 === e24) {
      return "End Time cannot be identical to Start Time";
    }

    if (meetingPlatform === "--- Select An Option ---") {
      return "Please select a valid Meeting Platform (Google Meet or Zoom)";
    }
    if (!privateMeetingUrl.trim()) return "Meeting Joining URL is required";

    // URL domain validation
    try {
      const parsed = new URL(privateMeetingUrl.trim());
      if (parsed.protocol !== "https:") {
        return "Meeting URL must begin with https://";
      }
      const host = parsed.hostname.toLowerCase();
      if (meetingPlatform === "Google Meet" && host !== "meet.google.com") {
        return "Google Meet URL must have the hostname meet.google.com";
      }
      if (meetingPlatform === "Zoom" && host !== "zoom.us" && !host.endsWith(".zoom.us")) {
        return "Zoom URL must have a valid zoom.us domain";
      }
    } catch (e) {
      return "Please enter a valid HTTPS meeting joining URL";
    }

    const seats = parseInt(totalSeats, 10);
    if (isNaN(seats) || seats <= 0) return "Total Seats must be at least 1";
    if (!language.trim()) return "Event Language is required";
    if (learningOutcomes.length === 0) return "At least one Learning Outcome is required";
    if (!targetAudience.trim()) return "Target Audience is required";
    if (!prerequisites.trim()) return "Prerequisites are required";
    if (agenda.length === 0) return "At least one Agenda item is required";
    if (!hostName.trim()) return "Host Name is required";
    if (!hostDesignation.trim()) return "Host Designation is required";
    if (!hostBio.trim()) return "Host Biography is required";

    return null;
  };

  const handleSave = async (targetStatus) => {
    setErrorMessage("");
    const validationErr = validateAllFields();
    if (validationErr) {
      setErrorMessage(validationErr);
      return;
    }

    try {
      setSubmitting(true);
      const cleanTags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const s24 = format12To24(startHour, startMinute, startMeridian);
      const e24 = format12To24(endHour, endMinute, endMeridian);
      const dur = calculateDurationInMinutes(s24, e24);

      const payload = {
        title: title.trim(),
        slug: slug.trim(),
        shortDescription: shortDescription.trim(),
        longDescription: longDescription.trim(),
        bannerImage: bannerSecureUrl,
        bannerImagePublicId: bannerPublicId,
        category: category.trim(),
        tags: cleanTags,
        eventDate: eventDate.trim(),
        startTime: s24,
        endTime: e24,
        durationMinutes: dur || parseInt(durationMinutes, 10) || 60,
        timezone,
        format,
        meetingPlatform,
        privateMeetingUrl: privateMeetingUrl.trim(),
        language: language.trim(),
        totalSeats: parseInt(totalSeats, 10),
        learningOutcomes,
        targetAudience: targetAudience.trim(),
        prerequisites: prerequisites.trim(),
        agenda,
        hostInformation: {
          name: hostName.trim(),
          designation: hostDesignation.trim(),
          bio: hostBio.trim()
        },
        status: targetStatus
      };

      const isEdit = Boolean(eventToEdit?._id);
      const endpoint = isEdit ? `/admin/events/${eventToEdit._id}` : "/admin/events";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && data.success) {
        if (isEdit && onEventUpdated) {
          onEventUpdated(data.event);
        } else if (onEventCreated) {
          onEventCreated(data.event);
        }
        onClose();
      } else {
        setErrorMessage(data.message || `Failed to ${isEdit ? "update" : "create"} event`);
      }
    } catch (err) {
      console.error("Save event error:", err);
      setErrorMessage("Network error saving event. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-event-modal-overlay" onClick={onClose}>
      <div className="create-event-modal" onClick={(e) => e.stopPropagation()}>
        {/* MODAL HEADER */}
        <div className="modal-header">
          <div className="header-text-group">
            <span className="modal-badge">{eventToEdit ? "Edit Session" : "New Session"}</span>
            <h2>{eventToEdit ? "Edit Event" : "Create New Event"}</h2>
            <p>
              {eventToEdit
                ? "Update event details, schedule times, seat capacity, or meeting settings."
                : "Configure complete details, schedule, seat capacity, and meeting settings."}
            </p>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* ERROR NOTIFICATION */}
        {errorMessage && (
          <div className="modal-error-alert">
            <FaExclamationTriangle />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* MODAL BODY WITH STRUCTURED SECTIONS */}
        <div className="modal-body">
          {/* SECTION A: BASIC INFORMATION */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section A — Basic Event Information</h3>

            <div className="form-row-two">
              <div className="input-group">
                <label className="input-label">
                  Event Title <span className="star">*</span>
                </label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="e.g. Masterclass on React 19 & Next.js Architecture"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">
                  Event Slug <span className="star">*</span>
                </label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="e.g. masterclass-react-19-architecture"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSlugEdited(true);
                  }}
                  required
                />
                <span className="field-hint">Used in URLs: /events/eventdetail/:slug</span>
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">
                Short Description <span className="star">*</span>
              </label>
              <textarea
                rows={2}
                className="modal-textarea"
                placeholder="Brief high-impact summary shown on public cards and previews..."
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                required
              />
            </div>

            <div className="input-group">
              <label className="input-label">
                Long Description <span className="star">*</span>
              </label>
              <textarea
                rows={5}
                className="modal-textarea"
                placeholder="Comprehensive description of the masterclass, topics covered, and structure..."
                value={longDescription}
                onChange={(e) => setLongDescription(e.target.value)}
                required
              />
            </div>

            <div className="form-row-two">
              <div className="input-group">
                <label className="input-label">
                  Category <span className="star">*</span>
                </label>
                <select
                  className="modal-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="Web Development">Web Development</option>
                  <option value="System Design">System Design</option>
                  <option value="Cloud & DevOps">Cloud & DevOps</option>
                  <option value="Artificial Intelligence">Artificial Intelligence</option>
                  <option value="Career & Tech Mentorship">Career & Tech Mentorship</option>
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">Tags (comma-separated)</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="React, Architecture, Full Stack"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* SECTION B: CLOUDINARY BANNER UPLOAD */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section B — Cloudinary Event Banner Upload</h3>

            <div className="upload-dropzone">
              {bannerPreview ? (
                <div className="banner-preview-box">
                  <img src={bannerPreview} alt="Uploaded Banner Preview" className="preview-image" />
                  <div className="preview-overlay">
                    <label className="reupload-btn">
                      Change Image
                      <input type="file" accept="image/*" onChange={handleBannerSelect} hidden />
                    </label>
                  </div>
                </div>
              ) : (
                <label className="dropzone-label">
                  <FaCloudUploadAlt className="upload-icon" />
                  <span className="upload-title">
                    {uploadingImage ? "Uploading to Cloudinary..." : "Click to select and upload banner image"}
                  </span>
                  <span className="upload-sub">Supports JPG, PNG, and WebP (Max 8MB). Uploads to portfolio/events.</span>
                  <input type="file" accept="image/*" onChange={handleBannerSelect} hidden disabled={uploadingImage} />
                </label>
              )}
            </div>

            {bannerSecureUrl && (
              <div className="upload-success-indicator">
                <FaCheck /> Cloudinary Asset Confirmed:{" "}
                <span className="url-snippet">{bannerSecureUrl.substring(0, 50)}...</span>
              </div>
            )}
          </div>

          {/* SECTION C: DATE & TIME */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section C — Event Date & Time (IST — GMT+05:30)</h3>

            <div className="form-row-three">
              <div className="input-group">
                <label className="input-label">
                  Event Date <span className="star">*</span>
                </label>
                <input
                  type="date"
                  className="modal-input"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">
                  Start Time (IST) <span className="star">*</span>
                </label>
                <div className="time-picker-control">
                  <select
                    className="time-picker-select hour-select"
                    value={startHour}
                    onChange={(e) => setStartHour(e.target.value)}
                    aria-label="Start Time Hour"
                  >
                    {HOURS_12.map((h) => (
                      <option key={`start-h-${h}`} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="time-picker-separator">:</span>
                  <select
                    className="time-picker-select minute-select"
                    value={startMinute}
                    onChange={(e) => setStartMinute(e.target.value)}
                    aria-label="Start Time Minute"
                  >
                    {MINUTES_60.map((m) => (
                      <option key={`start-m-${m}`} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    className="time-picker-select ampm-select"
                    value={startMeridian}
                    onChange={(e) => setStartMeridian(e.target.value)}
                    aria-label="Start Time AM/PM"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">
                  End Time (IST) <span className="star">*</span>
                </label>
                <div className="time-picker-control">
                  <select
                    className="time-picker-select hour-select"
                    value={endHour}
                    onChange={(e) => setEndHour(e.target.value)}
                    aria-label="End Time Hour"
                  >
                    {HOURS_12.map((h) => (
                      <option key={`end-h-${h}`} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="time-picker-separator">:</span>
                  <select
                    className="time-picker-select minute-select"
                    value={endMinute}
                    onChange={(e) => setEndMinute(e.target.value)}
                    aria-label="End Time Minute"
                  >
                    {MINUTES_60.map((m) => (
                      <option key={`end-m-${m}`} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    className="time-picker-select ampm-select"
                    value={endMeridian}
                    onChange={(e) => setEndMeridian(e.target.value)}
                    aria-label="End Time AM/PM"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-row-two">
              <div className="input-group">
                <label className="input-label">Duration (Minutes)</label>
                <input
                  type="number"
                  min="15"
                  className="modal-input"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                />
              </div>

              <div className="input-group">
                <label className="input-label">Timezone</label>
                <input type="text" className="modal-input" value="Asia/Kolkata (GMT+05:30)" disabled />
              </div>
            </div>
          </div>

          {/* SECTION D, E, F: FORMAT, MEETING PLATFORM & JOINING URL */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section D & E & F — Meeting Platform & Joining URL</h3>

            <div className="form-row-two">
              <div className="input-group">
                <label className="input-label">
                  Event Format <span className="star">*</span>
                </label>
                <select className="modal-select" value={format} onChange={(e) => setFormat(e.target.value)}>
                  <option value="Live Online">Live Online</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="In Person">In Person</option>
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">
                  Meeting Platform <span className="star">*</span>
                </label>
                <select
                  className="modal-select"
                  value={meetingPlatform}
                  onChange={(e) => setMeetingPlatform(e.target.value)}
                  required
                >
                  <option value="--- Select An Option ---">--- Select An Option ---</option>
                  <option value="Google Meet">Google Meet</option>
                  <option value="Zoom">Zoom</option>
                </select>
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">
                Private Meeting Joining URL <span className="star">*</span>
              </label>
              <input
                type="url"
                className="modal-input"
                placeholder={
                  meetingPlatform === "Google Meet"
                    ? "https://meet.google.com/abc-defg-hij"
                    : "https://us02web.zoom.us/j/123456789"
                }
                value={privateMeetingUrl}
                onChange={(e) => setPrivateMeetingUrl(e.target.value)}
                required
              />
              <span className="privacy-badge-note">
                🔒 Private URL: Never exposed publicly. Included only in confirmed customer emails.
              </span>
            </div>
          </div>

          {/* SECTION G & H: SEAT CAPACITY & LANGUAGE */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section G & H — Seat Capacity & Language</h3>

            <div className="form-row-two">
              <div className="input-group">
                <label className="input-label">
                  Total Seats <span className="star">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  className="modal-input"
                  value={totalSeats}
                  onChange={(e) => setTotalSeats(e.target.value)}
                  required
                />
                <span className="field-hint">Initial capacity. Can be expanded anytime.</span>
              </div>

              <div className="input-group">
                <label className="input-label">
                  Event Language <span className="star">*</span>
                </label>
                <input
                  type="text"
                  className="modal-input"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* SECTION I: LEARNING OUTCOMES */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section I — Learning Outcomes ("What You'll Learn")</h3>

            <div className="items-list">
              {learningOutcomes.map((item, idx) => (
                <div key={idx} className="item-pill-row">
                  <span>{item}</span>
                  <button type="button" className="remove-item-btn" onClick={() => handleRemoveOutcome(idx)}>
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>

            <div className="add-item-row">
              <input
                type="text"
                className="modal-input"
                placeholder="Add another learning outcome..."
                value={newOutcome}
                onChange={(e) => setNewOutcome(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOutcome();
                  }
                }}
              />
              <button type="button" className="add-btn" onClick={handleAddOutcome}>
                <FaPlus /> Add
              </button>
            </div>
          </div>

          {/* SECTION J & K: TARGET AUDIENCE & PREREQUISITES */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section J & K — Audience & Prerequisites</h3>

            <div className="input-group">
              <label className="input-label">
                Target Audience <span className="star">*</span>
              </label>
              <textarea
                rows={2}
                className="modal-textarea"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                required
              />
            </div>

            <div className="input-group">
              <label className="input-label">
                Prerequisites <span className="star">*</span>
              </label>
              <textarea
                rows={2}
                className="modal-textarea"
                value={prerequisites}
                onChange={(e) => setPrerequisites(e.target.value)}
                required
              />
            </div>
          </div>

          {/* SECTION L: AGENDA */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section L — Event Agenda</h3>

            <div className="agenda-builder-list">
              {agenda.map((ag, idx) => (
                <div key={idx} className="agenda-builder-item">
                  <div className="agenda-builder-badge">{ag.time || `Item ${idx + 1}`}</div>
                  <div className="agenda-builder-info">
                    <b>{ag.title}</b>
                    <p>{ag.description}</p>
                  </div>
                  <button type="button" className="remove-item-btn" onClick={() => handleRemoveAgenda(idx)}>
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>

            <div className="add-agenda-box">
              <div className="form-row-two">
                <input
                  type="text"
                  className="modal-input"
                  placeholder="Time slot (e.g. 07:00 PM - 07:30 PM)"
                  value={newAgendaTime}
                  onChange={(e) => setNewAgendaTime(e.target.value)}
                />
                <input
                  type="text"
                  className="modal-input"
                  placeholder="Agenda Topic Title *"
                  value={newAgendaTitle}
                  onChange={(e) => setNewAgendaTitle(e.target.value)}
                />
              </div>
              <input
                type="text"
                className="modal-input"
                placeholder="Short description of this session segment..."
                value={newAgendaDesc}
                onChange={(e) => setNewAgendaDesc(e.target.value)}
              />
              <button type="button" className="add-agenda-btn" onClick={handleAddAgenda}>
                <FaPlus /> Add Agenda Item
              </button>
            </div>
          </div>

          {/* SECTION M: HOST / SPEAKER */}
          <div className="modal-section-card">
            <h3 className="section-heading">Section M — Event Host / Speaker</h3>

            <div className="form-row-two">
              <div className="input-group">
                <label className="input-label">
                  Host Name <span className="star">*</span>
                </label>
                <input
                  type="text"
                  className="modal-input"
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">
                  Designation / Role <span className="star">*</span>
                </label>
                <input
                  type="text"
                  className="modal-input"
                  value={hostDesignation}
                  onChange={(e) => setHostDesignation(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">
                Host Biography <span className="star">*</span>
              </label>
              <textarea
                rows={3}
                className="modal-textarea"
                value={hostBio}
                onChange={(e) => setHostBio(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* MODAL FOOTER WITH PUBLISH & DRAFT ACTIONS */}
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <div className="modal-footer-actions">
            {eventToEdit ? (
              <button
                type="button"
                className="btn-publish"
                onClick={() => handleSave(eventToEdit.status || "Published")}
                disabled={submitting || uploadingImage}
              >
                {submitting ? "Saving Changes..." : "Update Event"}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-draft"
                  onClick={() => handleSave("Draft")}
                  disabled={submitting || uploadingImage}
                >
                  {submitting ? "Saving..." : "Save as Draft"}
                </button>
                <button
                  type="button"
                  className="btn-publish"
                  onClick={() => handleSave("Published")}
                  disabled={submitting || uploadingImage}
                >
                  {submitting ? "Publishing..." : "Publish Event 🚀"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEventModal;
